import styles from "./TeleOp.module.css";
import React from "react";
import {X} from 'lucide-react';
import arrowIcon from '../../static_files/arrow-back-double.svg';
import {useIsMobile} from "../../utils/utils";

import controlCmdBaseIcon from "../../static_files/control-cmd-base-icon.svg";
import controlCmdElbowIcon from "../../static_files/control-cmd-elbow-icon.svg";
import controlCmdGripIcon from "../../static_files/control-cmd-grip-icon.svg";
import controlCmdShoulderIcon from "../../static_files/control-cmd-shoulder-icon.svg";
import controlCmdHandRotateIcon from "../../static_files/control-cmd-hand-rotate-icon.svg";
import controlCmdToggleIcon from "../../static_files/control-cmd-toggle-icon.svg";

const ControlsPanel = ({isControlConnected, onEndTeleOp, isControlPanelOpen, setIsControlPanelOpen, remainingTime, embedded = false}) => {
    const isMobile = useIsMobile(1000)

    // Touch event handlers for mobile devices
    const handleTouchStart = (callback) => (e) => {
        e.preventDefault();
        callback();
    };
    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    };
    // Show reopen button when control panel is closed (disabled in embedded mode)
    if (!embedded && !isControlPanelOpen && !isMobile) {
        return (
            <button
                className={styles.openControlBtn}
                onClick={() => setIsControlPanelOpen(true)}
                onTouchStart={handleTouchStart(() => setIsControlPanelOpen(true))}
                style={{
                    position: 'fixed',
                    right: '0px',
                    top: '168px',
                    transformOrigin: 'left center',
                    backgroundColor: '#262626',
                    border: '1px solid #262626',
                    color: '#fff',
                    padding: '18px 20px',
                    borderRadius: '14px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'row',
                    lineHeight: '20px',
                    gap: '16px'
                }}
            >
                <img src={arrowIcon} alt='<<'/>
                Open Controls
            </button>
        );
    }if (!embedded && !isControlPanelOpen) {
        return (
            <button
                className={styles.openControlBtn}
                onClick={() => setIsControlPanelOpen(true)}
                onTouchStart={handleTouchStart(() => setIsControlPanelOpen(true))}
                 style={{
                    transformOrigin: 'left center',
                    backgroundColor: '#262626',
                    border: '1.5px solid #626263',
                    color: '#fff',
                    padding: '12px 20px',
                    borderRadius: '14px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'row',
                    lineHeight: '20px',
                    gap: '16px',
                    width: '90vw',
                    justifyContent: 'center',
                    marginTop:"20px",
                    marginLeft:'5vw'
                }}
            >
                Open Controls
            </button>
        );
    }

    const content = (
        <div style={{overflow:'auto'}}>
            <p className={styles.controlsInfo}>
                You're now in live control of the robot! Take time to learn the controls and explore. Your session expires in 5 minutes.
            </p>

            <div className={styles.controlsHeader}>
                <div className={styles.controlsHeaderButtons}>
                    <button
                        className={styles.endTeleOpBtn}
                        onClick={() => onEndTeleOp()}
                        onTouchStart={handleTouchStart(() => onEndTeleOp())}
                    >
                        End Tele-Operation
                    </button>
                </div>

                <div>
                    {remainingTime > 0 && (
                        <div className={styles.remainingTime}>
                            Your turn ends in: {formatTime(remainingTime)}
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.controlSectionDivider}></div>

            <div className={styles.controlLabel}>
                <span className={styles.greenDot}></span>
                Joint mode
            </div>

            <div className={styles.controlItem}>
                <div className={styles.controlKeys}>
                    <div className={styles.controlKeysCmdIconWrapper}>
                        <img src={controlCmdBaseIcon} alt="control keys for moving the robot base icon" className={styles.controlKeysBaseIcon} />
                    </div>
                    <span className={styles.keyText}>A</span>
                    <span className={styles.divider}>/</span>
                    <span className={styles.keyText}>D</span>
                </div>
                <span className={styles.controlName}>Base</span>
            </div>

            <div className={styles.controlItem}>
                <div className={styles.controlKeys}>
                    <div className={styles.controlKeysCmdIconWrapper}>
                        <img src={controlCmdElbowIcon} alt="control keys for moving the robot elbow" className={styles.controlKeysElbowIcon} />
                    </div>
                    <span className={styles.keyText}>↑</span>
                    <span className={styles.divider}>/</span>
                    <span className={styles.keyText}>↓</span>
                </div>
                <span className={styles.controlName}>Elbow</span>
            </div>

            <div className={styles.controlItem}>
                <div className={styles.controlKeys}>
                    <div className={styles.controlKeysCmdIconWrapper}>
                        <img src={controlCmdHandRotateIcon} alt="control keys for rotating the robot hand" className={styles.controlKeysHandRotateIcon} />
                    </div>
                    <span className={styles.keyText}>→</span>
                    <span className={styles.divider}>/</span>
                    <span className={styles.keyText}>←</span>
                </div>
                <span className={styles.controlName}>Hand Rotate</span>
            </div>

            <div className={styles.controlItem}>
                <div className={styles.controlKeys}>
                    <div className={styles.controlKeysCmdIconWrapper}>
                        <img src={controlCmdGripIcon} alt="control keys for gripping with the robot hand" className={styles.controlKeysGripIcon} />
                    </div>
                    <span className={styles.keyText}>Z</span>
                    <span className={styles.divider}>/</span>
                    <span className={styles.keyText}>X</span>
                </div>
                <span className={styles.controlName}>Grip Open/Close</span>
            </div>

            <div className={styles.controlItem}>
                <div className={styles.controlKeys}>
                    <div className={`${styles.controlKeysCmdIconWrapper} ${styles.controlKeysCmdTwoIconsWrapper}`}>
                        <span className={styles.keyIcon}>+</span>
                        <span className={styles.keyIcon}>-</span>
                    </div>
                    <span className={styles.keyText}>Q</span>
                    <span className={styles.divider}>/</span>
                    <span className={styles.keyText}>E</span>
                </div>
                <span className={styles.controlName}>Z-Axis</span>
            </div>

            <div className={styles.controlItem}>
                <div className={styles.controlKeys}>
                    <div className={`${styles.controlKeysCmdIconWrapper} ${styles.controlKeysCmdTwoIconsWrapper}`}>
                        <span className={styles.keyIcon}>↑</span>
                        <span className={styles.keyIcon}>↓</span>
                    </div>
                    <span className={styles.keyText}>W</span>
                    <span className={styles.divider}>/</span>
                    <span className={styles.keyText}>S</span>
                </div>
                <span className={styles.controlName}>Y-Axis</span>
            </div>

            {/*<div className={styles.controlItem}>*/}
            {/*    <div className={styles.controlKeys}>*/}
            {/*        <div className={`${styles.controlKeysCmdIconWrapper} ${styles.controlKeysCmdTwoIconsWrapper}`}>*/}
            {/*            <span className={styles.keyIcon}>+</span>*/}
            {/*            <span className={styles.keyIcon}>-</span>*/}
            {/*        </div>*/}
            {/*        <span className={styles.keyText}>Q</span>*/}
            {/*        <span className={styles.divider}>/</span>*/}
            {/*        <span className={styles.keyText}>E</span>*/}
            {/*    </div>*/}
            {/*    <span className={styles.controlName}>Z-Axis</span>*/}
            {/*</div>*/}
        </div>
    );

    if (embedded) {
        return content;
    }

    return (
        <div className={styles.controlsPanel}>
            <div style={{display:'flex', width:'100%', justifyContent:'flex-end'}}>
                <button
                    className={styles.closePanelBtn}
                    onClick={() => setIsControlPanelOpen(false)}
                    onTouchStart={handleTouchStart(() => setIsControlPanelOpen(false))}
                >
                    <X size={20}/>
                </button>
            </div>
            {content}
        </div>
    );
};

export default ControlsPanel
